import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFeatureConfig } from '@/hooks/useFeatureConfig';
import { usePlan } from '@/hooks/usePlan';
import { useAuth } from '@/hooks/useAuth';

/**
 * Map routes to admin-controlled hidden feature names (HIDDEN_FEATURES env var).
 * Redirects to /card when the feature is disabled server-wide.
 */
const ROUTE_TO_FEATURE: Record<string, string> = {
  '/ai-notes': 'ai-notes',
  '/analytics': 'analytics',
  '/qrana': 'qrana',
  '/blog': 'blog',
  '/api-keys': 'api-keys',
  '/subscription': 'subscription',
};

/**
 * Map routes to usePlan() boolean flags.
 * Redirects to /pricing when the user's plan does not include the feature.
 */
const ROUTE_TO_PLAN_FLAG: Partial<Record<string, keyof ReturnType<typeof usePlan>>> = {};

/**
 * Guards routes against:
 *  1. Admin-disabled features (HIDDEN_FEATURES env var)  → redirect to /card
 *  2. Plan-gated features (subscription plan)            → redirect to /pricing
 */
export function FeatureGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: featureConfig, isLoading } = useFeatureConfig();
  const { user } = useAuth();
  const plan = usePlan();

  useEffect(() => {
    const currentPath = location.pathname;

    // 1. Admin kill-switch: check HIDDEN_FEATURES env var
    if (!isLoading && featureConfig) {
      const hiddenFeatures = featureConfig.hiddenFeatures || [];
      const featureName = ROUTE_TO_FEATURE[currentPath];
      if (featureName && hiddenFeatures.includes(featureName)) {
        console.log(`🚫 [FeatureGuard] Admin-hidden feature blocked: ${featureName}`);
        navigate('/card', { replace: true });
        return;
      }
    }

    // 2. Plan gate: only evaluate once the user object is loaded
    //    (user===null while loading — avoids false redirect before auth resolves)
    if (user !== null && user !== undefined) {
      const planFlag = ROUTE_TO_PLAN_FLAG[currentPath];
      if (planFlag && !plan[planFlag]) {
        console.log(`🔒 [FeatureGuard] Plan gate: ${currentPath} requires ${planFlag} (current plan: ${plan.plan})`);
        navigate('/pricing', { replace: true });
      }
    }
  }, [location.pathname, featureConfig, isLoading, user, plan, navigate]);

  return <>{children}</>;
}

/**
 * Higher-order component to wrap routes that should be feature-protected
 */
export function withFeatureGuard<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> {
  return function FeatureGuardedComponent(props: P) {
    return (
      <FeatureGuard>
        <Component {...props} />
      </FeatureGuard>
    );
  };
}
